// 에삐 스티커북 어드민 타입 (내부 모델명은 eppi_costumes 유지)
// 백엔드 DTO: backend/src/main/kotlin/app/aivoca/server/domain/admin/eppi/AdminEppiCostumeData.kt
//
// V201 (2026-05-29): CostumeSlotType enum 폐기 → 동적 카테고리 + CostumeRenderKind(4값).
// ARTIFACT_LEFT/RIGHT 레거시 제거.

import {AiImageMode, AiImageProvider, AiImageRequestStatus} from './aiImage';
import {CostumeRenderKind} from './costumeCategory';

/**
 * TEXT_BUBBLE 텍스트 표현 메타.
 * 모두 nullable — TEXT_BUBBLE 슬롯에만 채워진다. 글자색은 고정(코코아 브라운)이라 컬럼 없음.
 * V200 painter 전환으로 V193 9-patch slice_* 필드는 입력/표시에서 제거됨 (백엔드 컬럼 DROP은 별도 PR).
 */
export interface TextBubbleMeta {
    padL?: number | null;
    padT?: number | null;
    padR?: number | null;
    padB?: number | null;
    textSizeSp?: number | null;
    maxWidthDp?: number | null;
    maxChars?: number | null;
    minWidthDp?: number | null;
    minHeightDp?: number | null;
}

// ── Painter (V200, 2026-05-28) ───────────────────────────────────────────────

export type BubbleTailDir = 'LEFT' | 'RIGHT' | 'NONE';

export const BUBBLE_TAIL_DIRS: BubbleTailDir[] = ['LEFT', 'RIGHT', 'NONE'];

export const BUBBLE_TAIL_DIR_LABEL_KO: Record<BubbleTailDir, string> = {
    LEFT: '왼쪽',
    RIGHT: '오른쪽',
    NONE: '없음',
};

export interface BubbleStyleSpec {
    code: string;
    shape: string;
    cornerRadius?: number;
    outlineWidth: number;
    tailLengthDp: number;
    tailWidthDp: number;
    supportedTailDirs: BubbleTailDir[];
}

export const FALLBACK_BUBBLE_STYLES: BubbleStyleSpec[] = [
    {
        code: 'CLASSIC_ROUNDED',
        shape: 'rounded_rect',
        cornerRadius: 14,
        outlineWidth: 2.0,
        tailLengthDp: 12,
        tailWidthDp: 10,
        supportedTailDirs: ['LEFT', 'RIGHT'],
    },
    {
        code: 'CLOUD_SOFT',
        shape: 'cloud',
        outlineWidth: 2.0,
        tailLengthDp: 14,
        tailWidthDp: 8,
        supportedTailDirs: ['LEFT', 'RIGHT'],
    },
    {
        code: 'SHARP_RECT',
        shape: 'rect',
        cornerRadius: 4,
        outlineWidth: 1.5,
        tailLengthDp: 10,
        tailWidthDp: 12,
        supportedTailDirs: ['LEFT', 'RIGHT'],
    },
    {
        code: 'THOUGHT_PUFF',
        shape: 'thought',
        outlineWidth: 2.0,
        tailLengthDp: 18,
        tailWidthDp: 0,
        supportedTailDirs: ['LEFT', 'RIGHT', 'NONE'],
    },
    {
        code: 'JAGGED_SHOUT',
        shape: 'jagged',
        outlineWidth: 2.5,
        tailLengthDp: 12,
        tailWidthDp: 14,
        supportedTailDirs: ['LEFT', 'RIGHT'],
    },
];

export interface BubblePainterMeta {
    bubbleStyleCode?: string | null;
    bubbleColorHex?: string | null;
    bubbleTailDir?: BubbleTailDir | null;
}

export const TEXT_BUBBLE_META_KEYS: (keyof TextBubbleMeta)[] = [
    'padL',
    'padT',
    'padR',
    'padB',
    'textSizeSp',
    'maxWidthDp',
    'maxChars',
];

// ── 스티커북 enum ────────────────────────────────────────────────────────────

export type PlacementType = 'BACK_WALL' | 'FLOOR';

export const PLACEMENT_TYPES: PlacementType[] = ['BACK_WALL', 'FLOOR'];

export const PLACEMENT_TYPE_LABEL_KO: Record<PlacementType, string> = {
    BACK_WALL: '벽 (BACK_WALL)',
    FLOOR: '바닥 (FLOOR)',
};

/**
 * V204 (2026-05-29): 5종(SMALL_DECOR/WALL_ITEM/MEDIUM/TALL/LARGE) → 3종(SMALL/MEDIUM/LARGE) 단순화.
 * 비율은 코스튬 이미지 자연 W/H 가 단일 source. 어드민은 사이즈 단일 축만 노출.
 */
export type CostumeSizePreset = 'SMALL' | 'MEDIUM' | 'LARGE';

export const COSTUME_SIZE_PRESETS: CostumeSizePreset[] = ['SMALL', 'MEDIUM', 'LARGE'];

export const SIZE_PRESET_LABEL_KO: Record<CostumeSizePreset, string> = {
    SMALL: '작은 소품',
    MEDIUM: '중간 소품',
    LARGE: '큰 소품',
};

/**
 * tile 값은 호환 fallback. 자연 비율은 finalize/upload 시 백엔드가 측정 → `imageAspectRatio` 컬럼에 저장.
 * AI 생성 비율은 모두 1:1 (오리지널 비율로 그려달라고 프롬프트에 명시).
 */
export const SIZE_PRESET_TILE_MAP: Record<
    CostumeSizePreset,
    {tileW: number; tileH: number; aspectRatio: string}
> = {
    SMALL: {tileW: 1, tileH: 1, aspectRatio: '1:1'},
    MEDIUM: {tileW: 2, tileH: 2, aspectRatio: '1:1'},
    LARGE: {tileW: 3, tileH: 3, aspectRatio: '1:1'},
};

export type BackgroundKind = 'SOLID' | 'TILE' | 'IMAGE';

export const BACKGROUND_KINDS: BackgroundKind[] = ['SOLID', 'TILE', 'IMAGE'];

export const BACKGROUND_KIND_LABEL_KO: Record<BackgroundKind, string> = {
    SOLID: '단색',
    TILE: '타일 패턴',
    IMAGE: '풀 일러스트',
};

// ── 코스튬 응답 / 요청 ──────────────────────────────────────────────────────────

export interface EppiCostume extends TextBubbleMeta, BubblePainterMeta {
    id: number;
    code: string;
    /** V201: 카테고리 id. null = 백필 미완료 레거시 (V202 적용 후 항상 채워짐). */
    categoryId: number | null;
    /** V201: 카테고리의 renderKind 미러. */
    renderKind: CostumeRenderKind;
    nameKo: string;
    nameEn: string;
    nameJa: string;
    nameVi: string;
    defaultPrompt?: string | null;
    priceXp: number;
    imageUrl: string | null;
    thumbnailUrl?: string;
    sortOrder: number;
    isActive: boolean;
    ownerCount: number;
    aiImageRequestId?: number;
    createdAt: string;
    updatedAt: string;
    bgKind?: BackgroundKind | null;
    bgColor?: string | null;
    bgTileSizePx?: number | null;
    tileW?: number | null;
    tileH?: number | null;
    placementType?: PlacementType | null;
    sizePreset?: CostumeSizePreset | null;
    /** V204: 이미지 자연 종횡비 (width/height). finalize/upload 시 백엔드가 측정. */
    imageAspectRatio?: number | null;
    isRoomEditorVisible: boolean;
    isDefault: boolean;
}

export interface EppiCostumePurchaseRecord {
    userId: number;
    purchasedAt: string;
    purchaseXpCost: number;
}

export interface EppiCostumeDetail extends EppiCostume {
    recentPurchases: EppiCostumePurchaseRecord[];
}

/**
 * renderKind 별 AI 이미지 그룹 메타 (V201).
 * 어드민 폼이 카테고리 선택 후 renderKind/placementType 에 따라 prefill 한다.
 */
export interface SlotGroupMeta {
    renderKind: CostumeRenderKind;
    placementType?: PlacementType | null;
    groupName: string;
    systemContext: string;
    defaultBaseImageUrl: string | null;
    defaultAspectRatio: string;
}

// ── Request DTOs ─────────────────────────────────────────────────────────────

export interface GenerateCostumeRequest extends TextBubbleMeta, BubblePainterMeta {
    code: string;
    /** V201: 카테고리 id. renderKind/AI 그룹/aspectRatio 자동 결정. */
    categoryId: number;
    nameKo: string;
    nameEn: string;
    nameJa: string;
    nameVi: string;
    defaultPrompt: string;
    priceXp: number;
    provider: AiImageProvider;
    mode: AiImageMode;
    imageCount: number;
    baseImageUrl?: string;
    sizePreset?: CostumeSizePreset;
    placementType?: PlacementType;
}

export interface GenerateCostumeResponse {
    aiImageRequestId: number;
    status: string;
    message: string;
}

export interface FinalizeCostumeRequest extends TextBubbleMeta, BubblePainterMeta {
    aiImageResultId: number;
    /** V201: 카테고리 id. */
    categoryId: number;
    nameKo: string;
    nameEn: string;
    nameJa: string;
    nameVi: string;
    defaultPrompt: string;
    priceXp: number;
    sortOrder: number;
    isActive: boolean;
    sizePreset?: CostumeSizePreset;
    placementType?: PlacementType;
    tileW?: number;
    tileH?: number;
}

export interface FinalizeCostumeResponse {
    id: number;
    code: string;
    imageUrl: string | null;
    updated: boolean;
}

export interface PatchCostumeRequest extends TextBubbleMeta, BubblePainterMeta {
    nameKo?: string;
    nameEn?: string;
    nameJa?: string;
    nameVi?: string;
    defaultPrompt?: string;
    priceXp?: number;
    sortOrder?: number;
    isActive?: boolean;
    /**
     * V201: 카테고리 변경. null/생략 = 변경 없음.
     * finalized(imageUrl != null) 코스튬은 동일 renderKind 카테고리로만 이동 — 다른 renderKind 시 400 5046.
     * draft 는 renderKind 변경 허용.
     */
    categoryId?: number;
    imageUrl?: string;
    bgKind?: BackgroundKind;
    bgColor?: string;
    bgTileSizePx?: number;
    sizePreset?: CostumeSizePreset;
    placementType?: PlacementType;
    tileW?: number;
    tileH?: number;
    isRoomEditorVisible?: boolean;
    isDefault?: boolean;
}

// ── Request History ──────────────────────────────────────────────────────────

export interface RequestHistoryResult {
    resultId: number;
    imageUrl: string;
    sequence: number;
}

export interface RequestHistoryItem {
    requestId: number;
    status: AiImageRequestStatus;
    provider: AiImageProvider;
    mode: AiImageMode;
    imageCount: number;
    baseImageUrl: string | null;
    userContext: string;
    errorMessage: string | null;
    createdAt: string;
    updatedAt: string;
    results: RequestHistoryResult[];
}
